/**
 * Commission-specific Cell Specifications
 *
 * Reusable cell specs for commission fields across all LOCs.
 * These specs handle the various commission types (percentage, pepm, heaped, graded, etc.)
 * with appropriate cell variants for each type.
 */

import {
  COMMISSION_TYPE_LABELS,
  CURRENT_COMMISSION_OPTIONS,
  REQUESTED_COMMISSION_OPTIONS,
} from "@platform/formatting-utils";
import { BaseBenefitPlanPolicyPeriod } from "@platform/openapi-types";

import { createContextConditionalEditable } from "../../../components/PlanEditor/utilities/agGridUtils";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../../components/PlanEditor/utilities/enumFormatters";

import {
  createDisabledAwareEnumFormatter,
  formatGradedCommissionValue,
  formatHeapedCommissionValue,
} from "./formatters";
import { CellSpec } from "./types";

/**
 * Current commission type dropdown
 * Includes "notApplicable" option in addition to standard commission types
 * Disabled if policy period is "newBusiness" or "alternate"
 */
export const currentCommissionType: CellSpec = {
  singleClickEdit: true,
  cellType: "dropdown",
  dataType: "string",
  cellEditor: "agRichSelectCellEditor",

  cellEditorParams: {
    allowTyping: true,
    filterList: true,
    searchType: "matchAny",
    values: CURRENT_COMMISSION_OPTIONS,
    formatValue: createDisabledAwareEnumFormatter(COMMISSION_TYPE_LABELS),
  },

  valueFormatter: createDisabledAwareEnumFormatter(COMMISSION_TYPE_LABELS),
  description: "Current commission type (includes Not Applicable option)",

  editable: createContextConditionalEditable("policyPeriod", [
    BaseBenefitPlanPolicyPeriod.newBusiness,
    BaseBenefitPlanPolicyPeriod.alternate,
  ]),

  validation: {
    required: true,
    allowedValues: CURRENT_COMMISSION_OPTIONS,
  },
};

/**
 * Requested commission type dropdown
 * Does NOT include "notApplicable" and "notDisclosed" options
 */
export const requestedCommissionType: CellSpec = {
  singleClickEdit: true,
  cellType: "dropdown",
  dataType: "string",
  cellEditor: "agRichSelectCellEditor",

  cellEditorParams: {
    allowTyping: true,
    filterList: true,
    searchType: "matchAny",
    values: REQUESTED_COMMISSION_OPTIONS,
    formatValue: createEnumFormatter(COMMISSION_TYPE_LABELS),
  },

  valueFormatter: createValueFormatter(COMMISSION_TYPE_LABELS),
  description: "Requested commission type",

  validation: {
    required: true,
    allowedValues: REQUESTED_COMMISSION_OPTIONS,
  },
};

/**
 * Heaped commission amount popover (2 percentage fields)
 * Used when commission type is "heaped"
 * Stores object with two values: { first, thereafter }
 * Values can be null/undefined - no percentages required
 */
export const heapedCommissionAmount: CellSpec = {
  cellType: "custom",
  dataType: "object",
  cellEditor: "HeapedCommissionEditor",
  singleClickEdit: true,
  valueFormatter: formatHeapedCommissionValue,

  description: "Heaped commission amounts (first year, thereafter)",
  defaultValue: null,

  validation: {
    required: false,
  },
};

/**
 * Graded commission amount popover
 * Used when commission type is "graded"
 * Stores a single "highest %" number, or null when there is no defined
 * percentage. A blank amount is allowed for graded commissions.
 */
export const gradedCommissionAmount: CellSpec = {
  cellType: "custom",
  dataType: "number",
  cellEditor: "GradedCommissionEditor",
  singleClickEdit: true,
  valueFormatter: formatGradedCommissionValue,

  description: "Graded commission amount (highest %, or none)",
  defaultValue: null,

  validation: {
    required: false,
  },
};
